import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import TeachingProgressPage from "@/pages/dashboard/TeachingProgressPage";
import { 
  School, 
  Users, 
  BookOpen, 
  GraduationCap, 
  TrendingUp,
  TrendingDown,
  LogOut,
  BarChart3,
  PieChart,
  FileText,
  Download,
  Printer,
  ChevronRight,
  Award,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Target,
  Calendar,
  ArrowUp,
  ArrowDown,
  Minus,
  UserPlus,
  UserCog,
  Mail,
  Phone,
  Search,
  Filter,
  BookMarked,
  ClipboardList,
  Layers,
  CheckSquare,
  Clock,
  Edit,
  Trash2,
  Eye,
  Settings,
  Lock,
  Key,
  Shield
} from "lucide-react";

const API_URL = '';

export default function CoordinatorDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSchoolFilter, setSelectedSchoolFilter] = useState("all");
  const [stats, setStats] = useState({
    totalSchools: 0,
    totalTeachers: 0,
    totalStudents: 0,
    totalClasses: 0
  });

  // Fetch real teachers data from API
  const [teachers, setTeachers] = useState([]);
  
  // Curriculum data (still mock until curriculum tracking is implemented)
  const [curriculumData, setCurriculumData] = useState([]);

  // Reports data state
  const [reportsData, setReportsData] = useState({
    summary: { total_schools: 0, schools_with_grades: 0, total_publications: 0, avg_grade_completion: 0 },
    publications_by_term: [],
    grade_completion_by_school: [],
    recent_publications: []
  });
  const [reportsLoading, setReportsLoading] = useState(false);

  // Account Settings state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 10;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 20;
    return Math.min(strength, 100);
  };

  const getStrengthLabel = (strength) => {
    if (strength < 30) return { label: 'Weak', color: 'bg-red-500' };
    if (strength < 60) return { label: 'Fair', color: 'bg-yellow-500' };
    if (strength < 80) return { label: 'Good', color: 'bg-blue-500' };
    return { label: 'Strong', color: 'bg-green-500' };
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (calculatePasswordStrength(passwordData.newPassword) < 50) {
      setPasswordError('Please choose a stronger password');
      return;
    }

    setPasswordChanging(true);

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswordSuccess(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      // Clear success message after 3 seconds
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordChanging(false);
    }
  };


  // Fetch real teachers when schools are loaded
  useEffect(() => {
    const fetchTeachers = async () => {
      if (user && schools.length > 0) {
        try {
          const token = localStorage.getItem("authToken");
          // Use "secondary" for Secondary Education to get both junior and senior secondary
          const schoolType = user.department === "Primary Education" ? "primary" : "secondary";
          
          const response = await axios.get(`${API_URL}/api/coordinator/teachers?school_type=${schoolType}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const realTeachers = response.data.teachers || [];
          // Transform fields for UI compatibility
          const teachersWithExtras = realTeachers.map(t => ({
            ...t,
            school: t.school_name,
            schoolId: t.school_id,
            classesAssigned: t.classes_assigned || 0
          }));
          
          setTeachers(teachersWithExtras);
        } catch (error) {
          console.error("Error fetching teachers:", error);
          // Fallback to mock data
          generateMockTeachers();
        }
      }
    };
    
    fetchTeachers();
  }, [user, schools]);

  // Fallback mock teachers generator
  const generateMockTeachers = () => {
    // No mock teachers - show empty if no real data
    setTeachers([]);
  };

  // Fetch real curriculum data from API
  const [attendanceData, setAttendanceData] = useState(null);
  
  useEffect(() => {
    const fetchCurriculumAndAttendance = async () => {
      if (user && schools.length > 0) {
        try {
          const token = localStorage.getItem("authToken");
          // Use "secondary" for Secondary Education to get both junior and senior secondary
          const schoolType = user.department === "Primary Education" ? "primary" : "secondary";
          
          // Fetch curriculum analytics
          const currResponse = await axios.get(`${API_URL}/api/coordinator/curriculum-analytics?school_type=${schoolType}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const currData = currResponse.data;
          
          // Transform curriculum data to match existing UI format
          const curriculumItems = currData.by_school.map((school) => {
            const docTypes = currData.by_document_type;
            return {
              schoolId: school.school_id,
              schoolName: school.school_name,
              yearlyPlanner: {
                status: getStatusFromRate(docTypes.find(d => d.document_type === 'yearly_planner')?.completion_rate || 0),
                completionRate: docTypes.find(d => d.document_type === 'yearly_planner')?.completion_rate || 0
              },
              schemeOfWork: {
                status: getStatusFromRate(docTypes.find(d => d.document_type === 'scheme_of_work')?.completion_rate || 0),
                completionRate: docTypes.find(d => d.document_type === 'scheme_of_work')?.completion_rate || 0
              },
              unitPlans: {
                status: getStatusFromRate(docTypes.find(d => d.document_type === 'unit_plan')?.completion_rate || 0),
                completionRate: docTypes.find(d => d.document_type === 'unit_plan')?.completion_rate || 0
              },
              lessonPlans: {
                status: getStatusFromRate(docTypes.find(d => d.document_type === 'lesson_plan')?.completion_rate || 0),
                completionRate: docTypes.find(d => d.document_type === 'lesson_plan')?.completion_rate || 0
              },
              overallAlignment: school.avg_completion || 0,
              issues: currData.schools_with_issues.find(s => s.school_id === school.school_id)?.total_issues || 0
            };
          });
          setCurriculumData(curriculumItems);
          
          // Fetch attendance analytics
          const attResponse = await axios.get(`${API_URL}/api/coordinator/attendance-analytics?school_type=${schoolType}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setAttendanceData(attResponse.data);
          
          // Update performance data with real attendance rate
          setPerformanceData(prev => ({
            ...prev,
            attendanceRate: attResponse.data.overall.attendance_rate || prev.attendanceRate,
            previousAttendance: prev.previousAttendance
          }));
          
        } catch (error) {
          console.error("Error fetching curriculum/attendance data:", error);
          // Fallback to mock curriculum data
          generateMockCurriculum();
        }
      }
    };
    
    fetchCurriculumAndAttendance();
  }, [user, schools]);

  // Fetch reports data when tab is active
  useEffect(() => {
    const fetchReportsData = async () => {
      if (user && activeTab === 'reports') {
        setReportsLoading(true);
        try {
          const token = localStorage.getItem("authToken");
          // Use "secondary" for Secondary Education to get both junior and senior secondary
          const schoolType = user.department === "Primary Education" ? "primary" : "secondary";
          
          const response = await axios.get(`${API_URL}/api/coordinator/reports-analytics?school_type=${schoolType}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setReportsData(response.data);
        } catch (error) {
          console.error("Error fetching reports data:", error);
          // Keep default empty state
        } finally {
          setReportsLoading(false);
        }
      }
    };
    
    fetchReportsData();
  }, [user, activeTab]);

  // Helper function to determine status from completion rate
  const getStatusFromRate = (rate) => {
    if (rate >= 80) return 'complete';
    if (rate >= 30) return 'in-progress';
    return 'pending';
  };

  // Fallback curriculum data (no random values)
  const generateMockCurriculum = () => {
    if (schools.length > 0) {
      const curriculumItems = schools.map((school) => ({
        schoolId: school.id,
        schoolName: school.name,
        schoolType: school.school_type,
        yearlyPlanner: { status: 'pending', lastUpdated: '-', completionRate: 0 },
        schemeOfWork: { status: 'pending', lastUpdated: '-', completionRate: 0 },
        unitPlans: { status: 'pending', lastUpdated: '-', completionRate: 0 },
        lessonPlans: { status: 'pending', lastUpdated: '-', completionRate: 0 },
        overallAlignment: 0,
        issues: 0
      }));
      setCurriculumData(curriculumItems);
    }
  };

  // Performance data from real API values
  const [performanceData, setPerformanceData] = useState({
    provinceAverage: 0,
    previousAverage: 0,
    passRate: 0,
    previousPassRate: 0,
    attendanceRate: 0,
    previousAttendance: 0
  });

  const fetchSchools = async (userData) => {
    try {
      const token = localStorage.getItem("authToken");
      // Use "secondary" for Secondary Education to get both junior and senior secondary
      const schoolType = userData.department === "Primary Education" ? "primary" : "secondary";
      
      // Fetch real stats from coordinator API
      const statsResponse = await axios.get(`${API_URL}/api/coordinator/stats?school_type=${schoolType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats({
        totalSchools: statsResponse.data.total_schools || 0,
        totalTeachers: statsResponse.data.total_teachers || 0,
        totalStudents: statsResponse.data.total_students || 0,
        totalClasses: statsResponse.data.total_classes || 0
      });
      
      // Update performance data with real values if available
      if (statsResponse.data.overall_average || statsResponse.data.overall_pass_rate) {
        setPerformanceData(prev => ({
          ...prev,
          provinceAverage: statsResponse.data.overall_average || prev.provinceAverage,
          passRate: statsResponse.data.overall_pass_rate || prev.passRate
        }));
      }
      
      // Fetch schools with analytics
      const schoolsResponse = await axios.get(`${API_URL}/api/coordinator/schools?school_type=${schoolType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const schoolsData = schoolsResponse.data.schools || [];
      
      // Add real data to each school (no random mock data)
      const schoolsWithPerformance = schoolsData.map((school, idx) => ({
        ...school,
        performance: {
          average: school.average_score || 0,
          previousAverage: 0,
          passRate: school.pass_rate || 0,
          attendanceRate: school.attendance_rate || 0,
          topSubject: school.top_subject || 'N/A',
          weakSubject: school.weak_subject || 'N/A',
          rank: idx + 1
        }
      }));

      setSchools(schoolsWithPerformance);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching schools:", error);
      // Fallback to old method if new API fails
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_URL}/api/schools`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const allSchools = response.data.schools || [];
        let filteredSchools = [];

        if (userData.department === "Primary Education") {
          filteredSchools = allSchools.filter(s => s.school_type === "primary");
        } else if (userData.department === "Secondary Education") {
          filteredSchools = allSchools.filter(
            s => s.school_type === "secondary" || s.school_type === "junior_secondary" || s.school_type === "senior_secondary"
          );
        }

        const schoolsWithPerformance = filteredSchools.map((school, idx) => ({
          ...school,
          performance: {
            average: school.average_score || 0,
            previousAverage: 0,
            passRate: school.pass_rate || 0,
            attendanceRate: school.attendance_rate || 0,
            topSubject: 'N/A',
            weakSubject: 'N/A',
            rank: idx + 1
          }
        }));

        setSchools(schoolsWithPerformance);
        
        setStats({
          totalSchools: filteredSchools.length,
          totalTeachers: filteredSchools.reduce((sum, s) => sum + (s.teacher_count || 0), 0),
          totalStudents: filteredSchools.reduce((sum, s) => sum + (s.student_count || 0), 0),
          totalClasses: filteredSchools.reduce((sum, s) => sum + (s.class_count || 0), 0)
        });
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const initDashboard = async () => {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) {
        navigate("/coordinator/login");
        return;
      }

      const userData = JSON.parse(storedUser);
      if (userData.role !== "coordinator") {
        navigate("/");
        return;
      }

      setUser(userData);
      await fetchSchools(userData);
    };
    
    initDashboard();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/coordinator/login");
  };

  const getSchoolTypeLabel = (type) => {
    switch(type) {
      case "primary": return "Primary School";
      case "senior_secondary": return "Senior Secondary";
      default: return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || type;
    }
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (current < previous) return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const getTrendColor = (current, previous) => {
    if (current > previous) return "text-green-600";
    if (current < previous) return "text-red-600";
    return "text-gray-600";
  };

  const getGradeColor = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-blue-500";
    if (score >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  const printReport = (school = null) => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString('en-GB', { 
      day: 'numeric', month: 'long', year: 'numeric' 
    });
    
    let reportContent = '';
    
    if (school) {
      // Individual school report
      reportContent = `
        <h2 style="color: #1e40af;">${school.name}</h2>
        <p><strong>Type:</strong> ${getSchoolTypeLabel(school.school_type)}</p>
        <p><strong>Status:</strong> ${school.status}</p>
        
        <h3>Overview</h3>
        <table>
          <tr><td>Total Teachers</td><td><strong>${school.teacher_count || 0}</strong></td></tr>
          <tr><td>Total Students</td><td><strong>${school.student_count || 0}</strong></td></tr>
          <tr><td>Total Classes</td><td><strong>${school.class_count || 0}</strong></td></tr>
        </table>
        
        <h3>Performance Summary</h3>
        <table>
          <tr><td>Current Average</td><td><strong>${school.performance?.average || 0}%</strong></td></tr>
          <tr><td>Previous Average</td><td>${school.performance?.previousAverage || 0}%</td></tr>
          <tr><td>Pass Rate</td><td><strong>${school.performance?.passRate || 0}%</strong></td></tr>
          <tr><td>Attendance Rate</td><td><strong>${school.performance?.attendanceRate || 0}%</strong></td></tr>
          <tr><td>Top Performing Subject</td><td>${school.performance?.topSubject || 'N/A'}</td></tr>
          <tr><td>Needs Improvement</td><td>${school.performance?.weakSubject || 'N/A'}</td></tr>
        </table>
      `;
    } else {
      // Province-wide report
      const schoolRows = schools.map(s => `
        <tr>
          <td>${s.name}</td>
          <td>${s.teacher_count || 0}</td>
          <td>${s.student_count || 0}</td>
          <td>${s.performance?.average || 0}%</td>
          <td>${s.performance?.passRate || 0}%</td>
          <td>${s.performance?.attendanceRate || 0}%</td>
        </tr>
      `).join('');
      
      reportContent = `
        <h2 style="color: #1e40af;">Provincial Education Report</h2>
        <p><strong>Department:</strong> ${user?.department}</p>
        
        <h3>Province Summary</h3>
        <table>
          <tr><td>Total Schools</td><td><strong>${stats.totalSchools}</strong></td></tr>
          <tr><td>Total Teachers</td><td><strong>${stats.totalTeachers}</strong></td></tr>
          <tr><td>Total Students</td><td><strong>${stats.totalStudents}</strong></td></tr>
          <tr><td>Total Classes</td><td><strong>${stats.totalClasses}</strong></td></tr>
        </table>
        
        <h3>Performance Overview</h3>
        <table>
          <tr><td>Province Average Score</td><td><strong>${performanceData.provinceAverage}%</strong></td></tr>
          <tr><td>Province Pass Rate</td><td><strong>${performanceData.passRate}%</strong></td></tr>
          <tr><td>Average Attendance</td><td><strong>${performanceData.attendanceRate}%</strong></td></tr>
        </table>
        
        <h3>School-by-School Breakdown</h3>
        <table border="1" style="border-collapse: collapse; width: 100%;">
          <thead style="background: #e5e7eb;">
            <tr>
              <th style="padding: 8px;">School</th>
              <th style="padding: 8px;">Teachers</th>
              <th style="padding: 8px;">Students</th>
              <th style="padding: 8px;">Avg Score</th>
              <th style="padding: 8px;">Pass Rate</th>
              <th style="padding: 8px;">Attendance</th>
            </tr>
          </thead>
          <tbody>
            ${schoolRows}
          </tbody>
        </table>
      `;
    }
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${school ? school.name : 'Provincial'} Performance Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 20px; margin-bottom: 20px; }
          .logo { font-size: 24px; font-weight: bold; color: #1e40af; }
          h2, h3 { color: #1f2937; margin-top: 25px; }
          table { width: 100%; margin: 15px 0; }
          td, th { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">📚 Penama Education Portal</div>
          <p>Performance Report</p>
          <p style="color: #6b7280; font-size: 14px;">Generated: ${currentDate}</p>
        </div>
        
        ${reportContent}
        
        <div class="footer">
          <p>Penama Provincial Education Office</p>
          <p>This report was generated automatically from the Penama Education Portal</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 border-b sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/10 backdrop-blur rounded-lg flex items-center justify-center border border-white/20">
              <span className="text-xl font-bold text-white">P</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Penama Education Portal</h1>
              <p className="text-xs text-blue-200">Coordinator Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-blue-200">{user?.department}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-white/30 text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name}!
          </h2>
          <p className="text-gray-600">
            {user?.department === "Primary Education" 
              ? "Managing primary schools across Penama Province"
              : "Managing secondary education across Penama Province"}
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <School className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Reports</span>
            </TabsTrigger>
            <TabsTrigger value="teachers" className="flex items-center gap-2">
              <UserCog className="w-4 h-4" />
              <span className="hidden sm:inline">Teachers</span>
            </TabsTrigger>
            <TabsTrigger value="curriculum" className="flex items-center gap-2">
              <BookMarked className="w-4 h-4" />
              <span className="hidden sm:inline">Curriculum</span>
            </TabsTrigger>
            <TabsTrigger value="teaching" className="flex items-center gap-2" data-testid="coordinator-tab-teaching">
              <BookMarked className="w-4 h-4" />
              <span className="hidden sm:inline">Teaching Progress</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="teaching">
            <TeachingProgressPage />
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 shadow-lg shadow-blue-600/20">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-blue-100">Total Schools</CardTitle>
                  <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center">
                    <School className="w-5 h-5 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats.totalSchools}</div>
                  <p className="text-xs text-blue-200 mt-1">Under your oversight</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0 shadow-lg shadow-emerald-600/20">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-emerald-100">Total Teachers</CardTitle>
                  <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats.totalTeachers}</div>
                  <p className="text-xs text-emerald-200 mt-1">Across all schools</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-violet-600 to-violet-700 border-0 shadow-lg shadow-violet-600/20">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-violet-100">Total Students</CardTitle>
                  <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats.totalStudents}</div>
                  <p className="text-xs text-violet-200 mt-1">Enrolled students</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500 to-orange-600 border-0 shadow-lg shadow-amber-500/20">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-amber-100">Total Classes</CardTitle>
                  <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats.totalClasses}</div>
                  <p className="text-xs text-amber-100 mt-1">Active classes</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions - Teacher Attendance */}
            <Card className="bg-gradient-to-r from-slate-800 to-slate-900 border-0 shadow-xl overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full -translate-y-1/2 translate-x-1/3"></div>
              <CardContent className="py-5 relative z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">Teacher Attendance Tracking</h3>
                      <p className="text-sm text-slate-300">Monitor real-time attendance status across all your schools</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => navigate('/dashboard/student-enrollment')}
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10 px-5"
                      data-testid="coord-assign-students-btn"
                    >
                      <GraduationCap className="w-4 h-4 mr-2" />
                      Assign Students to Subjects
                    </Button>
                    <Button 
                      onClick={() => navigate('/coordinator/teacher-attendance')}
                      className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/30 px-6"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      View Attendance
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Schools List */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Schools</h3>
              
              {schools.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <School className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No schools found under your oversight.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {schools.map((school, idx) => {
                    const colors = [
                      { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-50', text: 'text-blue-700', shadow: 'shadow-blue-500/10' },
                      { bg: 'from-emerald-500 to-teal-600', light: 'bg-emerald-50', text: 'text-emerald-700', shadow: 'shadow-emerald-500/10' },
                      { bg: 'from-violet-500 to-purple-600', light: 'bg-violet-50', text: 'text-violet-700', shadow: 'shadow-violet-500/10' },
                      { bg: 'from-rose-500 to-pink-600', light: 'bg-rose-50', text: 'text-rose-700', shadow: 'shadow-rose-500/10' },
                    ];
                    const c = colors[idx % colors.length];
                    return (
                    <Card key={school.id} className={`hover:shadow-xl transition-all duration-300 border-0 shadow-lg ${c.shadow} overflow-hidden`}>
                      <div className={`h-1.5 bg-gradient-to-r ${c.bg}`}></div>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 bg-gradient-to-br ${c.bg} rounded-xl flex items-center justify-center shadow-md`}>
                              <School className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{school.name}</CardTitle>
                              <p className={`text-sm ${c.text} font-medium`}>{getSchoolTypeLabel(school.school_type)}</p>
                            </div>
                          </div>
                          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                            {school.status}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className={`${c.light} rounded-lg p-3 text-center`}>
                            <p className={`text-2xl font-bold ${c.text}`}>{school.teacher_count || 0}</p>
                            <p className="text-xs text-gray-600 font-medium">Teachers</p>
                          </div>
                          <div className={`${c.light} rounded-lg p-3 text-center`}>
                            <p className={`text-2xl font-bold ${c.text}`}>{school.student_count || 0}</p>
                            <p className="text-xs text-gray-600 font-medium">Students</p>
                          </div>
                          <div className={`${c.light} rounded-lg p-3 text-center`}>
                            <p className={`text-2xl font-bold ${c.text}`}>{school.class_count || 0}</p>
                            <p className="text-xs text-gray-600 font-medium">Classes</p>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className={`flex-1 ${c.text} border-current hover:${c.light}`}
                            onClick={() => {
                              setSelectedSchool(school);
                              setActiveTab('analytics');
                            }}
                          >
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Analytics
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => printReport(school)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Report
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Province-wide Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-blue-100">Province Average Score</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-4xl font-bold">{performanceData.provinceAverage}%</div>
                      <div className="flex items-center mt-1 text-blue-100">
                        {getTrendIcon(performanceData.provinceAverage, performanceData.previousAverage)}
                        <span className="ml-1 text-sm">
                          {(performanceData.provinceAverage - performanceData.previousAverage).toFixed(1)}% from last term
                        </span>
                      </div>
                    </div>
                    <TrendingUp className="w-12 h-12 text-blue-200 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-green-100">Pass Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-4xl font-bold">{performanceData.passRate}%</div>
                      <div className="flex items-center mt-1 text-green-100">
                        {getTrendIcon(performanceData.passRate, performanceData.previousPassRate)}
                        <span className="ml-1 text-sm">
                          {(performanceData.passRate - performanceData.previousPassRate)}% from last term
                        </span>
                      </div>
                    </div>
                    <CheckCircle className="w-12 h-12 text-green-200 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-purple-100">Attendance Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-4xl font-bold">{performanceData.attendanceRate}%</div>
                      <div className="flex items-center mt-1 text-purple-100">
                        {getTrendIcon(performanceData.attendanceRate, performanceData.previousAttendance)}
                        <span className="ml-1 text-sm">
                          {(performanceData.attendanceRate - performanceData.previousAttendance)}% from last term
                        </span>
                      </div>
                    </div>
                    <Users className="w-12 h-12 text-purple-200 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* School Performance Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  School Performance Comparison
                </CardTitle>
                <CardDescription>Compare performance metrics across all schools</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {schools.map((school, idx) => (
                    <div key={school.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-blue-500'
                          }`}>
                            {idx + 1}
                          </div>
                          <div>
                            <h4 className="font-semibold">{school.name}</h4>
                            <p className="text-sm text-gray-500">{school.student_count} students</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">{school.performance?.average}%</div>
                          <div className={`text-sm flex items-center justify-end ${getTrendColor(school.performance?.average, school.performance?.previousAverage)}`}>
                            {getTrendIcon(school.performance?.average, school.performance?.previousAverage)}
                            <span className="ml-1">{(school.performance?.average - school.performance?.previousAverage)}%</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Average Score</span>
                            <span className="text-xs font-medium">{school.performance?.average}%</span>
                          </div>
                          <Progress value={school.performance?.average} className="h-2" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Pass Rate</span>
                            <span className="text-xs font-medium">{school.performance?.passRate}%</span>
                          </div>
                          <Progress value={school.performance?.passRate} className="h-2" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Attendance</span>
                            <span className="text-xs font-medium">{school.performance?.attendanceRate}%</span>
                          </div>
                          <Progress value={school.performance?.attendanceRate} className="h-2" />
                        </div>
                      </div>
                      
                      <div className="flex gap-4 mt-3 pt-3 border-t">
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-gray-600">Best: {school.performance?.topSubject}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          <span className="text-sm text-gray-600">Needs work: {school.performance?.weakSubject}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Subject Performance */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <Award className="w-5 h-5" />
                    Top Performing Subjects
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {['Mathematics', 'English', 'Science', 'ICT', 'Social Science'].map((subject, idx) => (
                      <div key={subject} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{idx + 1}.</span>
                          <span className="font-medium">{subject}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={85 - idx * 5} className="w-24 h-2" />
                          <span className="text-sm font-medium w-12 text-right">{85 - idx * 5}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" />
                    Subjects Needing Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {['Agriculture', 'Arts', 'Physical Education', 'Music', 'French'].map((subject, idx) => (
                      <div key={subject} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{idx + 1}.</span>
                          <span className="font-medium">{subject}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={55 - idx * 3} className="w-24 h-2" />
                          <span className="text-sm font-medium w-12 text-right">{55 - idx * 3}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            {reportsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading reports data...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Reports Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <School className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                      <div className="text-2xl font-bold">{reportsData.summary?.total_schools || stats.totalSchools}</div>
                      <p className="text-sm text-gray-500">Total Schools</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                      <div className="text-2xl font-bold">{reportsData.summary?.schools_with_grades || 0}</div>
                      <p className="text-sm text-gray-500">Schools with Grades</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <FileText className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                      <div className="text-2xl font-bold">{reportsData.summary?.total_publications || 0}</div>
                      <p className="text-sm text-gray-500">Published Reports</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Target className="w-8 h-8 mx-auto text-orange-500 mb-2" />
                      <div className="text-2xl font-bold">{reportsData.summary?.avg_grade_completion || 0}%</div>
                      <p className="text-sm text-gray-500">Avg Grade Completion</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Generate Reports Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Generate Reports
                    </CardTitle>
                    <CardDescription>Download or print performance reports for your schools</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Province-wide Report */}
                      <Card className="border-2 border-blue-200 bg-blue-50">
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                              <PieChart className="w-8 h-8 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">Provincial Summary Report</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                Overview of all {stats.totalSchools} schools with performance metrics, 
                                attendance data, and comparative analysis.
                              </p>
                              <div className="flex gap-2 mt-4">
                                <Button onClick={() => printReport()} className="flex-1">
                                  <Printer className="w-4 h-4 mr-2" />
                                  Print Report
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Term Report */}
                      <Card className="border-2 border-green-200 bg-green-50">
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-green-100 rounded-lg">
                              <Calendar className="w-8 h-8 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">Term Progress Report</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {reportsData.publications_by_term?.length > 0 
                                  ? `${reportsData.publications_by_term.length} terms with published reports`
                                  : 'No term reports published yet'}
                              </p>
                              <div className="flex gap-2 mt-4">
                                <Button variant="outline" onClick={() => printReport()} className="flex-1">
                                  <Printer className="w-4 h-4 mr-2" />
                                  Print Report
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>

                {/* Grade Completion by School */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="w-5 h-5" />
                      Grade Completion Status
                    </CardTitle>
                    <CardDescription>Track grading progress across all schools</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {reportsData.grade_completion_by_school?.length > 0 ? (
                      <div className="space-y-4">
                        {reportsData.grade_completion_by_school.map((school) => (
                          <div key={school.school_id} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <School className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <h4 className="font-semibold">{school.school_name}</h4>
                                  <p className="text-sm text-gray-500">
                                    {school.total_students} students • {school.years_covered} year levels
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <Badge variant={
                                  school.grade_completion_pct >= 80 ? "default" : 
                                  school.grade_completion_pct >= 50 ? "secondary" : "destructive"
                                }>
                                  {school.grade_completion_pct}% Complete
                                </Badge>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-gray-500">Students Graded</span>
                                  <span className="text-xs font-medium">{school.graded_students}/{school.total_students}</span>
                                </div>
                                <Progress value={school.grade_completion_pct} className="h-2" />
                              </div>
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-gray-500">Assessments Complete</span>
                                  <span className="text-xs font-medium">{school.assessments_complete}/{school.total_assessments}</span>
                                </div>
                                <Progress value={school.assessment_completion_pct} className="h-2" />
                              </div>
                            </div>
                            
                            <div className="flex justify-end mt-3">
                              <Button variant="outline" size="sm" onClick={() => printReport(schools.find(s => s.id === school.school_id))}>
                                <Printer className="w-4 h-4 mr-2" />
                                Print School Report
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No grade data available yet</p>
                        <p className="text-sm text-gray-400 mt-1">Grades will appear here once teachers start entering marks</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Publications */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Recent Report Publications
                    </CardTitle>
                    <CardDescription>Track when report cards were published by principals</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {reportsData.recent_publications?.length > 0 ? (
                      <div className="space-y-3">
                        {reportsData.recent_publications.map((pub) => (
                          <div key={pub.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-green-100 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium">{pub.school_name}</p>
                                <p className="text-sm text-gray-500">
                                  {pub.term} {pub.year} • Published by {pub.publisher_name}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">
                                {pub.published_at ? new Date(pub.published_at).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric'
                                }) : 'Unknown date'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No reports published yet</p>
                        <p className="text-sm text-gray-400 mt-1">Reports will appear here once principals publish report cards</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Individual School Reports */}
                <Card>
                  <CardHeader>
                    <CardTitle>Individual School Reports</CardTitle>
                    <CardDescription>Generate detailed reports for each school</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {schools.map((school) => (
                        <div key={school.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <School className="w-5 h-5 text-gray-500" />
                            <div>
                              <p className="font-medium">{school.name}</p>
                              <p className="text-sm text-gray-500">{school.student_count} students • {school.teacher_count} teachers</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={school.performance?.average >= 70 ? "default" : school.performance?.average >= 50 ? "secondary" : "destructive"}>
                              {school.performance?.average}% avg
                            </Badge>
                            <Button variant="outline" size="sm" onClick={() => printReport(school)}>
                              <Printer className="w-4 h-4 mr-2" />
                              Print
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Teacher Management Tab */}
          <TabsContent value="teachers" className="space-y-6">
            {/* Teacher Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{teachers.length}</p>
                      <p className="text-sm text-gray-500">Total Teachers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{teachers.filter(t => t.status === 'active').length}</p>
                      <p className="text-sm text-gray-500">Active Teachers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{teachers.filter(t => t.status === 'on-leave').length}</p>
                      <p className="text-sm text-gray-500">On Leave</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <BookOpen className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{teachers.reduce((sum, t) => sum + (t.classesAssigned || 0), 0)}</p>
                      <p className="text-sm text-gray-500">Total Classes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filter */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCog className="w-5 h-5" />
                  Teacher Management
                </CardTitle>
                <CardDescription>View and manage teachers across all schools in your province</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search teachers by name, email, or subject..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={selectedSchoolFilter}
                    onChange={(e) => setSelectedSchoolFilter(e.target.value)}
                    className="px-4 py-2 border rounded-md bg-white min-w-[200px]"
                  >
                    <option value="all">All Schools</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>{school.name}</option>
                    ))}
                  </select>
                </div>

                {/* Teachers Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-3 font-semibold text-gray-700 border-b">Teacher</th>
                        <th className="text-left p-3 font-semibold text-gray-700 border-b">School</th>
                        <th className="text-left p-3 font-semibold text-gray-700 border-b">Subject/Department</th>
                        <th className="text-left p-3 font-semibold text-gray-700 border-b">Classes</th>
                        <th className="text-left p-3 font-semibold text-gray-700 border-b">Status</th>
                        <th className="text-left p-3 font-semibold text-gray-700 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teachers
                        .filter(teacher => {
                          const matchesSearch = searchTerm === '' || 
                            teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (teacher.subject && teacher.subject.toLowerCase().includes(searchTerm.toLowerCase()));
                          const matchesSchool = selectedSchoolFilter === 'all' || teacher.schoolId === selectedSchoolFilter;
                          return matchesSearch && matchesSchool;
                        })
                        .map((teacher) => (
                          <tr key={teacher.id} className="hover:bg-gray-50 border-b" data-testid={`teacher-row-${teacher.id}`}>
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="font-semibold text-blue-600">
                                    {teacher.name.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium">{teacher.name}</p>
                                  <p className="text-sm text-gray-500">{teacher.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <p className="text-sm">{teacher.school || teacher.school_name}</p>
                            </td>
                            <td className="p-3">
                              <Badge variant="outline">{teacher.subject || 'General'}</Badge>
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-semibold">{teacher.classesAssigned || 0}</span>
                            </td>
                            <td className="p-3">
                              <Badge variant={teacher.status === 'active' ? 'default' : 'secondary'}>
                                {teacher.status === 'active' ? 'Active' : 'On Leave'}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" title="View Details">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" title="Edit Teacher">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {teachers.filter(t => {
                  const matchesSearch = searchTerm === '' || 
                    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    t.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (t.subject && t.subject.toLowerCase().includes(searchTerm.toLowerCase()));
                  const matchesSchool = selectedSchoolFilter === 'all' || t.schoolId === selectedSchoolFilter;
                  return matchesSearch && matchesSchool;
                }).length === 0 && (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No teachers found matching your criteria</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Teachers by Subject Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Teachers by Subject</CardTitle>
                <CardDescription>Distribution of teachers across different subjects</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['English', 'Mathematics', 'Science', 'ICT', 'Social Science', 'Agriculture', 'Arts', 'Physical Education'].map(subject => {
                    const count = teachers.filter(t => t.subject === subject).length;
                    return (
                      <div key={subject} className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-sm text-gray-600">{subject}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Curriculum Alignment Tab */}
          <TabsContent value="curriculum" className="space-y-6">
            {/* Curriculum Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckSquare className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {curriculumData.filter(c => c.overallAlignment >= 80).length}
                      </p>
                      <p className="text-sm text-gray-500">Fully Aligned</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {curriculumData.filter(c => c.overallAlignment >= 60 && c.overallAlignment < 80).length}
                      </p>
                      <p className="text-sm text-gray-500">In Progress</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {curriculumData.reduce((sum, c) => sum + c.issues, 0)}
                      </p>
                      <p className="text-sm text-gray-500">Total Issues</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Target className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {(curriculumData.reduce((sum, c) => sum + c.overallAlignment, 0) / (curriculumData.length || 1)).toFixed(0)}%
                      </p>
                      <p className="text-sm text-gray-500">Avg Alignment</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Curriculum Status by School */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookMarked className="w-5 h-5" />
                  Curriculum Alignment by School
                </CardTitle>
                <CardDescription>Track curriculum documentation status across all schools</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {curriculumData.map((school) => (
                    <div key={school.schoolId} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <School className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold">{school.schoolName}</h4>
                            <p className="text-sm text-gray-500">{getSchoolTypeLabel(school.schoolType)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold">{school.overallAlignment}%</span>
                            <Badge variant={school.overallAlignment >= 80 ? 'default' : school.overallAlignment >= 60 ? 'secondary' : 'destructive'}>
                              {school.overallAlignment >= 80 ? 'Aligned' : school.overallAlignment >= 60 ? 'In Progress' : 'Needs Work'}
                            </Badge>
                          </div>
                          {school.issues > 0 && (
                            <p className="text-sm text-red-500 flex items-center gap-1 justify-end mt-1">
                              <AlertTriangle className="w-3 h-3" />
                              {school.issues} issues flagged
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Curriculum Components Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {/* Yearly Planner */}
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Yearly Planner</span>
                            {school.yearlyPlanner.status === 'complete' ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : school.yearlyPlanner.status === 'in-progress' ? (
                              <Clock className="w-4 h-4 text-amber-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                          <Progress value={school.yearlyPlanner.completionRate} className="h-2 mb-1" />
                          <p className="text-xs text-gray-500">{school.yearlyPlanner.completionRate}% complete</p>
                        </div>

                        {/* Scheme of Work */}
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Scheme of Work</span>
                            {school.schemeOfWork.status === 'complete' ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : school.schemeOfWork.status === 'in-progress' ? (
                              <Clock className="w-4 h-4 text-amber-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                          <Progress value={school.schemeOfWork.completionRate} className="h-2 mb-1" />
                          <p className="text-xs text-gray-500">{school.schemeOfWork.completionRate}% complete</p>
                        </div>

                        {/* Unit Plans */}
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Unit Plans</span>
                            {school.unitPlans.status === 'complete' ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : school.unitPlans.status === 'in-progress' ? (
                              <Clock className="w-4 h-4 text-amber-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                          <Progress value={school.unitPlans.completionRate} className="h-2 mb-1" />
                          <p className="text-xs text-gray-500">{school.unitPlans.completionRate}% complete</p>
                        </div>

                        {/* Lesson Plans */}
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Lesson Plans</span>
                            {school.lessonPlans.status === 'complete' ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : school.lessonPlans.status === 'in-progress' ? (
                              <Clock className="w-4 h-4 text-amber-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                          <Progress value={school.lessonPlans.completionRate} className="h-2 mb-1" />
                          <p className="text-xs text-gray-500">{school.lessonPlans.completionRate}% complete</p>
                        </div>
                      </div>

                      <div className="flex justify-end mt-3 pt-3 border-t">
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {curriculumData.length === 0 && (
                  <div className="text-center py-8">
                    <BookMarked className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No curriculum data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Province-wide Curriculum Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    Well-Aligned Schools
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {curriculumData
                      .filter(c => c.overallAlignment >= 80)
                      .sort((a, b) => b.overallAlignment - a.overallAlignment)
                      .slice(0, 5)
                      .map((school, idx) => (
                        <div key={school.schoolId} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{idx + 1}.</span>
                            <span className="font-medium">{school.schoolName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Progress value={school.overallAlignment} className="w-24 h-2" />
                            <span className="text-sm font-medium w-12 text-right">{school.overallAlignment}%</span>
                          </div>
                        </div>
                      ))}
                    {curriculumData.filter(c => c.overallAlignment >= 80).length === 0 && (
                      <p className="text-gray-500 text-sm">No schools are fully aligned yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" />
                    Schools Needing Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {curriculumData
                      .filter(c => c.overallAlignment < 80)
                      .sort((a, b) => a.overallAlignment - b.overallAlignment)
                      .slice(0, 5)
                      .map((school, idx) => (
                        <div key={school.schoolId} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{idx + 1}.</span>
                            <span className="font-medium">{school.schoolName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Progress value={school.overallAlignment} className="w-24 h-2" />
                            <span className="text-sm font-medium w-12 text-right">{school.overallAlignment}%</span>
                          </div>
                        </div>
                      ))}
                    {curriculumData.filter(c => c.overallAlignment < 80).length === 0 && (
                      <p className="text-gray-500 text-sm">All schools are well-aligned!</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Change Password Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-blue-600" />
                    Change Password
                  </CardTitle>
                  <CardDescription>
                    Update your account password. Choose a strong password with at least 8 characters.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    {/* Current Password */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Current Password</label>
                      <div className="relative">
                        <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                          placeholder="Enter current password"
                          className="pl-10"
                          data-testid="current-password-input"
                        />
                      </div>
                    </div>

                    {/* New Password */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">New Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                          placeholder="Enter new password"
                          className="pl-10"
                          data-testid="new-password-input"
                        />
                      </div>
                      {/* Password Strength Indicator */}
                      {passwordData.newPassword && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all duration-300 ${getStrengthLabel(calculatePasswordStrength(passwordData.newPassword)).color}`}
                                style={{ width: `${calculatePasswordStrength(passwordData.newPassword)}%` }}
                              />
                            </div>
                            <span className={`text-xs font-medium ${
                              calculatePasswordStrength(passwordData.newPassword) < 30 ? 'text-red-600' :
                              calculatePasswordStrength(passwordData.newPassword) < 60 ? 'text-yellow-600' :
                              calculatePasswordStrength(passwordData.newPassword) < 80 ? 'text-blue-600' : 'text-green-600'
                            }`}>
                              {getStrengthLabel(calculatePasswordStrength(passwordData.newPassword)).label}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Use 8+ characters with uppercase, lowercase, numbers & symbols
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Confirm New Password</label>
                      <div className="relative">
                        <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                          placeholder="Confirm new password"
                          className="pl-10"
                          data-testid="confirm-password-input"
                        />
                      </div>
                      {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <XCircle className="w-3 h-3" /> Passwords do not match
                        </p>
                      )}
                      {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && passwordData.newPassword && (
                        <p className="text-xs text-green-500 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Passwords match
                        </p>
                      )}
                    </div>

                    {/* Error Message */}
                    {passwordError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <p className="text-sm text-red-700">{passwordError}</p>
                      </div>
                    )}

                    {/* Success Message */}
                    {passwordSuccess && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <p className="text-sm text-green-700">Password changed successfully!</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={passwordChanging}
                      data-testid="change-password-btn"
                    >
                      {passwordChanging ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          Changing Password...
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Change Password
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Account Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" />
                    Account Information
                  </CardTitle>
                  <CardDescription>
                    Your coordinator account details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Name</p>
                          <p className="font-medium">{user?.name || 'Coordinator'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <p className="font-medium">{user?.email || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Department</p>
                          <p className="font-medium">{user?.department || 'Education'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <School className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Schools Managed</p>
                          <p className="font-medium">{stats.totalSchools} schools</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                          <GraduationCap className="w-5 h-5 text-cyan-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Role</p>
                          <p className="font-medium">Education Coordinator</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
